import { LeadCorridor, LeadProduct } from "interfaces";
import { ClientRegistryMatch } from "interfaces/enums";

// Copy for the "already in the funnel" notice, shared by the create-lead modal
// and the leads list. `blocking` true means a new lead must NOT be created for
// this company (already a client, or already being onboarded); false is an
// advisory collision the broker can override. Strings are English source keys
// resolved through i18n at render. The owning broker is never named — the
// registry_check contract returns only the match state.
export const REGISTRY_NOTICE: Record<
  Exclude<ClientRegistryMatch, ClientRegistryMatch.NONE>,
  { title: string; body: string; blocking: boolean }
> = {
  [ClientRegistryMatch.ACTIVE_CLIENT]: {
    title: "This company is already an active Crosspoint client.",
    body: "It is not available for new leads. Check with your manager if you believe this is an error.",
    blocking: true,
  },
  [ClientRegistryMatch.ONBOARDING_IN_PROGRESS]: {
    title: "This company is already being onboarded.",
    body: "An onboarding link has already been issued for it. Check with your manager before creating a new lead.",
    blocking: true,
  },
  [ClientRegistryMatch.EXISTING_LEAD]: {
    title: "A lead for this company already exists.",
    body: "It may belong to you or another broker. Continue only if you are sure this is a separate opportunity.",
    blocking: false,
  },
};

export const isBlockingMatch = (match?: ClientRegistryMatch) =>
  match === ClientRegistryMatch.ACTIVE_CLIENT ||
  match === ClientRegistryMatch.ONBOARDING_IN_PROGRESS;

// Drives which KB document checklist gates the lead (corporate §5.1 vs the
// PFAE client checklist) — the engine keys document_requirements on it.
export const CLIENT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "CORPORATE_ENTITY", label: "Persona Moral (corporate)" },
  {
    value: "INDIVIDUAL_WITH_BUSINESS_ACTIVITY",
    label: "Persona Física con Actividad Empresarial (PFAE)",
  },
];

export const PRODUCT_OPTIONS: Array<{ value: LeadProduct; label: string }> = [
  { value: "INTERNATIONAL_PAYMENTS", label: "International Payments" },
  { value: "MEX_DIVISA", label: "MexDivisa" },
  { value: "BOTH", label: "Both" },
];

export const CORRIDOR_OPTIONS: Array<{ value: LeadCorridor; label: string }> = [
  { value: "MX_ASIA", label: "MX ↔ Asia" },
  { value: "MX_US", label: "MX ↔ US" },
  { value: "MX_EUROPE", label: "MX ↔ Europe" },
  { value: "DOMESTIC_MX", label: "Domestic MX (USD ↔ MXN)" },
];

// Declared transactional profile — capture options; Compliance builds the
// client's transactional profile in the engine from these declared values.
// String options double as the STORED value and the t() display key, so
// they are English-canonical (the engine keeps them verbatim) and each
// locale file carries the translation.
export const FREQUENCY_OPTIONS = [
  "Daily",
  "Weekly",
  "Biweekly",
  "Monthly",
  "Sporadic",
];

export const COUNTERPARTIES_OPTIONS = ["1–5", "6–20", "21–50", "More than 50"];

export const VOLUME_RANGE_OPTIONS = [
  "<$100k",
  "$100k–$500k",
  "$500k–$1M",
  ">$1M",
];
